import * as styles from "./JobFields.css";

import { useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
} from "@frontend/components/atoms/Combobox";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Field,
  FieldContent,
  FieldContext,
  FieldError,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import type { IconName } from "@frontend/components/atoms/Icon";
import { Switch } from "@frontend/components/atoms/Switch";
import { useField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { ReferenceField } from "@frontend/shared/reference-field";
import { useLingui } from "@lingui/react/macro";

import { jobEditorAtom, jobForm, referencesAtom } from "../stores/editor";
import type { Employee } from "../types";

const fields = jobForm.fields;

const gridFields = makeFieldContext({
  orientation: "vertical",
  grouped: false,
});

function Group({
  icon,
  legend,
  children,
}: {
  icon: IconName;
  legend: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Fieldset variant="section">
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <FieldContext.Provider value={gridFields}>
        <div className={styles.fields}>{children}</div>
      </FieldContext.Provider>
    </Fieldset>
  );
}

export function JobFields(): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const references = useAtomValue(referencesAtom);
  const project = useAtomValue(jobEditorAtom).project;

  const worker = useField(fields.workerId);
  const jobFunction = useField(fields.functionId);
  const category = useField(fields.categoryId);
  const startDate = useField(fields.startDate);
  const stopDate = useField(fields.stopDate);
  const refundLunch = useField(fields.refundLunch);
  const refundRide = useField(fields.refundRide);
  const inContract = useField(fields.inContract);

  const chosen =
    references.workers.find(({ id }) => id === worker.value) ?? null;

  function chooseWorker(next: Employee | null) {
    worker.setValue(next?.id ?? null);

    if (null != next?.functionId) {
      jobFunction.setValue(next.functionId);
    }

    if (null != next?.categoryId) {
      category.setValue(next.categoryId);
    }
  }

  return (
    <div className={styles.groups}>
      <Group icon="UserRound" legend={t`Worker`}>
        <Field className={styles.span.worker}>
          <FieldLabel>{t`Worker`}</FieldLabel>

          <FieldContent>
            <Combobox<Employee>
              items={references.workers}
              value={chosen}
              itemToStringLabel={(item) => item?.label ?? ""}
              isItemEqualToValue={(a, b) => a.id === b.id}
              onValueChange={chooseWorker}
            >
              <ComboboxInput placeholder={t`Choose a worker`} />

              <ComboboxContent empty={t`No match.`}>
                {(item: Employee) => (
                  <ComboboxItem key={item.id} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxContent>
            </Combobox>

            <FieldError match={Boolean(worker.error)}>
              {worker.error}
            </FieldError>
          </FieldContent>
        </Field>

        <ReferenceField
          className={styles.span.jobFunction}
          label={t`Function`}
          placeholder={t`Choose a function`}
          items={references.functions}
          field={fields.functionId}
        />

        <ReferenceField
          className={styles.span.category}
          label={t`Category`}
          placeholder={t`Choose a category`}
          items={references.categories}
          field={fields.categoryId}
        />
      </Group>

      <Group icon="FileText" legend={t`Terms`}>
        <ReferenceField
          className={styles.span.holidays}
          label={t`Public holiday`}
          items={references.holidayActions}
          field={fields.holidayActionId}
        />

        <ReferenceField
          className={styles.span.billing}
          label={t`Billing`}
          items={references.billings}
          field={fields.billingId}
        />

        <Field className={styles.span.from}>
          <FieldLabel>{t`From`}</FieldLabel>

          <FieldContent>
            <DatePicker
              locale={locale}
              value={startDate.value ?? null}
              onValueChange={(next) => startDate.setValue(next)}
            >
              <DatePickerTrigger>
                <DatePickerValue
                  placeholder={project?.contractStartDate ?? t`As the contract`}
                />
              </DatePickerTrigger>

              <DatePickerContent />
            </DatePicker>

            <FieldError match={Boolean(startDate.error)}>
              {startDate.error}
            </FieldError>
          </FieldContent>
        </Field>

        <Field className={styles.span.to}>
          <FieldLabel>{t`To`}</FieldLabel>

          <FieldContent>
            <DatePicker
              locale={locale}
              value={stopDate.value ?? null}
              onValueChange={(next) => stopDate.setValue(next)}
            >
              <DatePickerTrigger>
                <DatePickerValue
                  placeholder={project?.contractStopDate ?? t`As the contract`}
                />
              </DatePickerTrigger>

              <DatePickerContent />
            </DatePicker>

            <FieldError match={Boolean(stopDate.error)}>
              {stopDate.error}
            </FieldError>
          </FieldContent>
        </Field>

        <div className={styles.switches}>
          <Switch
            label={t`Lunch refunded`}
            checked={true === refundLunch.value}
            onCheckedChange={(next) => refundLunch.setValue(next)}
          />

          <Switch
            label={t`Ride refunded`}
            checked={true === refundRide.value}
            onCheckedChange={(next) => refundRide.setValue(next)}
          />

          <Switch
            label={t`In the contract`}
            checked={true === inContract.value}
            onCheckedChange={(next) => inContract.setValue(next)}
          />
        </div>
      </Group>
    </div>
  );
}
